<template>
  <div>

    <modal
      :title="`${lpContext?.name}: ${$t('actions.edit_default_rates')}`"
      ref="uriCostModal"
      :submit="submit"
      :submitting="submitting"
    >

      <default-rate-fields-with-cdrs
        v-model="formState.defaultRates"
        :show-ids="canSeeIds"
        :collapsed="false"
      />

    </modal>

  </div>
</template>

<script setup lang="ts">

import { computed, inject, onBeforeMount, reactive, ref, useTemplateRef } from 'vue'

// components
import Modal from '../../../Modal.vue'
import DefaultRateFieldsWithCdrs from '../../../forms/rates/DefaultRateFieldsWithCdrs.vue'

// models
import { type BulkUpdateDefaultRatesRequestItem, DefaultRate } from '../../../../models/DefaultRate'

// misc
// @ts-ignore
import { closeDialog } from 'vue3-promise-dialog'
import type { UriContext, UriLpContext } from './types'
import type { DefaultRateFieldWithCdrsState, DefaultRateFieldsWithCdrsState, ChannelDefaultRateFieldState  } from '../../../../types/misc'
import type { Can } from '../../../../main'
import NotificationService from '../../../../services/NotificationService'


interface Props {
  locationProductId: number
  context: UriContext
}

const props = defineProps<Props>()

const $can = inject('$can') as Can
const canSeeIds = $can('seeids')

const lpContext = computed<UriLpContext>((): UriLpContext => {
  return props.context.lps.find(lpContext => lpContext.lpId === props.locationProductId)!
})

/**
 * Form state
 */

const formState = reactive<DefaultRateFieldsWithCdrsState>({defaultRates: []})

/**
 * Initialization
 */

onBeforeMount(() => {

  // populate formState

  const defaultCost = lpContext.value.defaultRate.defaultCost

  const dr: DefaultRateFieldWithCdrsState = {
    id: lpContext.value.defaultRate.drId,
    defaultCost: defaultCost,
    channelDefaultRates: [],
  }

  lpContext.value.channelFacilities.values().forEach(channelFacility => {

    const cdr = lpContext.value.defaultRate.channelDefaultRates.get(channelFacility.ID)
    const channelCost = cdr?.channelCost ?? null

    const formCdr: ChannelDefaultRateFieldState = {
      id: cdr?.cdrId ?? null,
      channelCost: channelCost,
      originalChannelCost: channelCost,
      channelFacility: channelFacility,
    }

    dr.channelDefaultRates.push(formCdr)

  })

  formState.defaultRates.push(dr)

})

/**
 * Submission
 */

const submitting = ref<boolean>(false)
const uriCostModal = useTemplateRef('uriCostModal')

async function submit() {

  await uriCostModal.value!.modalForm!.validate()

  if (!uriCostModal.value!.modalForm!.isValid) {
    return
  }

  // It's annoying to do this outside of the main validation, but I think we have to.
  const violatesSpotHero = formState.defaultRates.some(formDr => {
    return formDr.channelDefaultRates.some(formCdr => {
      return formCdr.channelFacility?.channel === 'spothero' && formCdr.channelCost && formCdr.channelCost > 250
    })
  })

  if (violatesSpotHero) {
    NotificationService.error('This would set a SpotHero channel rate to > $250')
    return
  }

  submitting.value = true

  const request: BulkUpdateDefaultRatesRequestItem[] = formState.defaultRates.map(formDr => {
    const requestDr: BulkUpdateDefaultRatesRequestItem = {
      defaultRateID: formDr.id!, // we know this will have an ID, because we don't allow the user to create new DRs via the URI UI
      defaultCost: formDr.defaultCost!,
      channelDefaultRates: formDr.channelDefaultRates.map(formCdr => {
        return {
          channelDefaultRateID: formCdr.id!,
          channelCost: formCdr.channelCost ? formCdr.channelCost : formDr.defaultCost!,
          channelFacilitiesID: formCdr.channelFacility.ID,
        }
      }),
    }
    return requestDr
  })

  DefaultRate.bulkUpdate(request)
    .then(() => {
      closeDialog(true)
    })
    .catch((e) => {
      console.error(e)
      NotificationService.error('Couldn\'t update default rates!')
    })
    .finally(() => {
      submitting.value = false
    })

}

</script>
